/* src/components/customGame/setup/CustomGameSetupPage.js */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSave, faFolderOpen, faPlusSquare } from '@fortawesome/free-solid-svg-icons';

// Import sidebar and panel components
import CustomGameSidebar from './CustomGameSidebar';
import GlobalSettingsPanel from './panels/GlobalSettingsPanel';
import StimulusPanelController from './panels/StimulusPanelController';
import SequenceRulesPanel from './panels/SequenceRulesPanel'; // *** IMPORTED ***
import TimingPanel from './panels/TimingPanel';             // *** IMPORTED ***
import FeedbackPanel from './panels/FeedbackPanel';           // *** IMPORTED ***
import ControlsPanel from './panels/ControlsPanel';           // *** IMPORTED ***
// import ResultsScoringPanel from './panels/ResultsScoringPanel'; // For later

import { useCustomGame } from '../../../contexts/customGame/CustomGameContext';

// Default Initial Configuration (structure remains the same from last update)
// ... (getDefaultConfiguration, PREDEFINED_PALETTES, PREDEFINED_SHAPE_SETS, PREDEFINED_SYMBOL_SETS remain the same) ...
const getDefaultConfiguration = () => ({
    presetMetadata: {
        name: "Untitled Custom Game",
        description: "",
        author: "",
        creationDate: new Date().toISOString(),
        customGameVersion: "1.0.0",
    },
    settings: {
        Session: {
            NValue: {
                Mode: "Fixed",
                FixedValue: 2,
                PerStimulusValue: {},
                AdaptiveSettings: { initialN: 1, increaseThreshold: 0.85, decreaseThreshold: 0.70 },
            },
            Termination: {
                Mode: "Fixed Trial Count",
                TrialCount: 60,
                DurationSeconds: 180,
            },
            RandomSeed: {
                Mode: "Automatic",
                Value: "dualnback",
            },
        },
        Stimulus: {
            EnabledTypes: ["Audio", "Position"],
            Audio: { SetType: "Letters", LetterSet: "Standard Consonants (Jaeggi)", WordList: "Theme: Objects", SimilarityMode: "None (Distinct Items)", SimilarityLevel: 0.5, Source: "Pre-recorded Pack", TTSVoice: "System Default", Spatialization: 0.0, Volume: 1.0 },
            Position: { GridSize: "3x3", GridWidth: 3, GridHeight: 3, AllowedLocations: [], StimulusShape: "Square", StimulusColor: "#3B82F6", GridAppearance: "Visible Borders", BackgroundColor: "#F3F4F6" },
            Color: { StimulusShape: "Central Square", PaletteType: "Predefined Set (Basic 8)", PaletteColors: PREDEFINED_PALETTES["Basic 8"], PaletteColorsUserString: PREDEFINED_PALETTES["Basic 8"].join(','), NumberOfColors: PREDEFINED_PALETTES["Basic 8"].length, SimilarityMetric: "HSL Distance", MinSimilarityDistance: 0.3 },
            Shape: { SetType: "Geometric (Simple)", ShapeList: PREDEFINED_SHAPE_SETS["Geometric (Simple)"], ShapeListUserString: PREDEFINED_SHAPE_SETS["Geometric (Simple)"].join(','), ComplexityControl: 0.5, RotationInvariance: true, SizeVariation: "Fixed Size", FillStyle: "Filled", LineThickness: 2, Color: "#4F46E5" },
            Symbolic: { SetType: "Phonologically Distinct Letters (Research)", SymbolList: PREDEFINED_SYMBOL_SETS["Phonologically Distinct Letters (Research)"] },
            Haptic: {},
            EmotionalFace: {},
        },
        Sequence: {
            Matches: { Mode: "Target Percentage", TargetPercentage: 0.25, FixedCount: 15, Distribution: "Equal Probability", PerStimulusTarget: {}, MinDistance: 1, MaxDistance: 10 },
            Lures: { Enabled: true, Types: ["N-1 Lure"], Mode: "Target Percentage (Overall)", TargetPercentage: { Overall: 0.15 }, FixedCount: { Overall: 9 }, Distribution: "Equal Probability" },
            Overlap: { ControlEnabled: true, TargetPercentage: 0.05, AllowedCombinations: [] },
            Constraints: { PreventImmediateRepeats: true, MinNonMatchRun: 0 },
            Structure: { Algorithm: "Standard Pseudorandom" },
        },
        Timing: {
            ISI: { Mode: "Fixed", FixedDurationMs: 2500, VariableMeanMs: 2500, VariableRangeMs: 500 },
            StimulusDuration: { Mode: "Fixed", FixedDurationMs: 500, VariableMeanMs: 500, VariableRangeMs: 100 },
            ResponseWindow: { Mode: "Entire Trial", CustomStartMs: 0, CustomEndMs: 3000 },
            Fixation: { Enabled: true, Appearance: "Crosshair (+)", Color: "#374151", DurationMs: 2500 },
        },
        Feedback: {
            Correctness: { Enabled: true, Modality: ["Visual"], VisualElement: "Stimulus Border", DurationMs: 200 },
            MissedMatch: { Enabled: true, Modality: ["Visual"], VisualElement: "Stimulus Border", DurationMs: 300, DelayMs: 500 },
            Acknowledgment: { Enabled: false, Modality: ["Visual"], VisualElement: "Subtle flash", DurationMs: 100 },
            Summary: { ShowEndOfBlock: true, ShowEndOfSession: true },
        },
        UI: {
            InGame: { ShowTrialCounter: true, ShowLiveScore: false, StimulusRenderStyle: "Flat" },
        },
        Audio: { 
             InGame: { FeedbackVolume: 0.7, CorrectFeedbackSound: "Default Beep", IncorrectFeedbackSound: "Default Buzz", MissedMatchFeedbackSound: "Warning Tone" }
        },
        Controls: {
            InputMethods: ["Keyboard"],
            ShowOnScreenButtons: true,
            KeyMapping: { Preset: "Default (A=Visual/Position, L=Audio)", Custom: {} },
        },
        Results: {
            PrimaryMetric: "d' (d-prime)",
            DisplayedMetrics: ["Accuracy", "RT", "d'"],
            DPrimeCalculationMethod: "Standard Correction (Add/Subtract 0.5/N)",
            Saving: { Mode: "Always Save (If Logged In)", AnonymizeData: false },
        },
    }
});

const PREDEFINED_PALETTES = {
    "Basic 8": ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#000000", "#FFFFFF"],
};
const PREDEFINED_SHAPE_SETS = {
    "Geometric (Simple)": ["Square", "Circle", "Triangle"],
};
const PREDEFINED_SYMBOL_SETS = {
    "Phonologically Distinct Letters (Research)": "B,K,Q,T,H,M,N,P,X,R",
};

// Component definition remains the same
const CustomGameSetupPage = () => {
    const navigate = useNavigate();
    const { setCustomGameConfig } = useCustomGame();

    const [activePanel, setActivePanel] = useState('global');
    const [configuration, setConfiguration] = useState(getDefaultConfiguration());

    const handlePanelChange = useCallback((panelKey) => {
        setActivePanel(panelKey);
    }, []);

    const handleConfigChange = useCallback((path, value) => {
        setConfiguration(prevConfig => {
            const keys = path.split('.');
            let currentLevel = JSON.parse(JSON.stringify(prevConfig));
            
            let targetObject = currentLevel;
            let keySegmentIndex = 0;

            if (keys[0] === 'settings' || keys[0] === 'presetMetadata') {
                targetObject = currentLevel[keys[0]];
                keySegmentIndex = 1; 
            } else {
                targetObject = currentLevel.settings;
            }

            for (let i = keySegmentIndex; i < keys.length - 1; i++) {
                if (!targetObject[keys[i]] || typeof targetObject[keys[i]] !== 'object') {
                    targetObject[keys[i]] = {}; 
                }
                targetObject = targetObject[keys[i]];
            }
            targetObject[keys[keys.length - 1]] = value; 
            return currentLevel;
        });
    }, []);

    useEffect(() => {
        console.log("CustomGameSetupPage mounted. Initial config:", JSON.parse(JSON.stringify(configuration)));
    }, []);


    const handlePlayGame = () => {
        console.log("Starting custom game with config:", configuration);
        if (setCustomGameConfig && typeof setCustomGameConfig === 'function') {
            setCustomGameConfig(configuration);
            navigate('/custom-game/play');
        } else {
            console.error("setCustomGameConfig is not available from useCustomGame context.");
        }
    };

    const handleSavePreset = () => {
        console.log("Save Preset clicked. Config:", configuration);
        alert("Save Preset functionality to be implemented!");
    };

    const handleLoadPreset = () => {
        console.log("Load Preset clicked.");
        alert("Load Preset functionality to be implemented!");
    };
    
    const handleSaveAsPreset = () => {
        console.log("Save As Preset clicked. Config:", configuration);
        alert("Save As Preset functionality to be implemented!");
    };


    const renderActivePanel = () => {
        const settings = configuration.settings || {};

        switch (activePanel) {
            case 'global':
                return <GlobalSettingsPanel config={settings.Session} onConfigChange={handleConfigChange} pathPrefix="Session" />;
            case 'stimuli':
                return <StimulusPanelController
                            config={settings.Stimulus}
                            onConfigChange={handleConfigChange}
                            pathPrefix="Stimulus"
                       />;
            case 'sequence': // *** NOW RENDERS SequenceRulesPanel ***
                return <SequenceRulesPanel
                            config={settings.Sequence}
                            onConfigChange={handleConfigChange}
                            pathPrefix="Sequence"
                       />;
            case 'timing': // *** NOW RENDERS TimingPanel ***
                return <TimingPanel
                            config={settings.Timing}
                            onConfigChange={handleConfigChange}
                            pathPrefix="Timing"
                       />;
            case 'feedback': // *** NOW RENDERS FeedbackPanel ***
                 return <FeedbackPanel
                            feedbackConfig={settings.Feedback}
                            uiInGameConfig={settings.UI?.InGame}
                            audioInGameConfig={settings.Audio?.InGame}
                            onConfigChange={handleConfigChange}
                            // No pathPrefix needed if FeedbackPanel handles full paths to settings.Feedback, settings.UI.InGame etc.
                       />;
            case 'controls': // *** NOW RENDERS ControlsPanel ***
                return <ControlsPanel
                            config={settings.Controls}
                            onConfigChange={handleConfigChange}
                            pathPrefix="Controls"
                            globalStimulusConfig={settings.Stimulus} // Pass stimulus config for dynamic key mapping
                       />;
            // case 'results': // Still for later
            //     return <ResultsScoringPanel config={settings.Results} onConfigChange={handleConfigChange} pathPrefix="Results" />;
            default:
                return <div className="p-6 text-gray-700">Select a category from the sidebar to configure.</div>;
        }
    };

    // JSX for header and layout remains the same
    return (
        <div className="flex flex-col h-full bg-gray-100">
            <header className="bg-white shadow-md p-3 sticky top-0 z-10">
                <div className="container mx-auto flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-800">
                            {configuration.presetMetadata.name || "Custom Game Setup"}
                        </h1>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleLoadPreset}
                            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faFolderOpen} className="mr-2" />
                            Load Preset
                        </button>
                        <button
                            onClick={handleSavePreset}
                            className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                            Save
                        </button>
                         <button
                            onClick={handleSaveAsPreset}
                            className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faPlusSquare} className="mr-2" />
                            Save As
                        </button>
                        <button
                            onClick={handlePlayGame}
                            className="px-4 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faPlay} className="mr-2" />
                            Play
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                <CustomGameSidebar activePanel={activePanel} onPanelChange={handlePanelChange} />
                <main className="flex-1 p-0 overflow-y-auto bg-white">
                    {renderActivePanel()}
                </main>
            </div>
        </div>
    );
};

export default CustomGameSetupPage;